import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { canonicalizeCognitoRole } from "../constants/Roles.constants";

export const AuthLogLevel = {
  Error: "error",
  Info: "info",
  Verbose: "verbose",
  Warning: "warning",
};

export const AuthEventType = {
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  ACQUIRE_TOKEN_SUCCESS: "ACQUIRE_TOKEN_SUCCESS",
};

export const AuthInteractionStatus = {
  None: "None",
  Login: "Login",
  AcquireToken: "AcquireToken",
  Logout: "Logout",
};

export class BrowserAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = "BrowserAuthError";
  }
}

export class InteractionRequiredAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = "InteractionRequiredAuthError";
  }
}

const isAuthDebugEnabled = () => {
  try {
    return window.localStorage.getItem("auth_debug") === "1";
  } catch (_) {
    return false;
  }
};

const debugAuthLog = (...args) => {
  if (isAuthDebugEnabled()) {
    console.info("[AUTH-DEBUG]", ...args);
  }
};

const safeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return [value];
  }
  return [];
};

const normalizeRoles = (claims) => {
  const groups = safeArray(claims["cognito:groups"]);
  const roles = safeArray(claims.roles);
  const customRoles = safeArray(claims["custom:roles"]);
  const merged = [...roles, ...groups, ...customRoles].map((r) =>
    canonicalizeCognitoRole(r),
  );
  return [...new Set(merged)];
};

const toAccount = (user) => {
  if (!user) {
    return null;
  }

  const claims = user.profile || {};
  const email =
    claims.email || claims["cognito:username"] || claims.username || "";

  return {
    homeAccountId: user.profile?.sub || claims.sub || "unknown",
    localAccountId: claims.sub || "unknown",
    name:
      claims.name ||
      [claims.given_name, claims.family_name].filter(Boolean).join(" ") ||
      email ||
      "User",
    username: email || claims.username || claims.sub || "unknown",
    idTokenClaims: {
      ...claims,
      roles: normalizeRoles(claims),
      signInName: claims.signInName || email,
    },
  };
};

export class CognitoAuthClient {
  constructor(config) {
    this.config = config;
    const cognitoDomain = config?.auth?.cognitoDomain;
    const normalizedDomain = cognitoDomain
      ? cognitoDomain.replace(/^https?:\/\//, "")
      : "";
    const domainBaseUrl = normalizedDomain ? `https://${normalizedDomain}` : "";
    const metadata = domainBaseUrl
      ? {
          issuer: config?.auth?.authority,
          authorization_endpoint: `${domainBaseUrl}/oauth2/authorize`,
          token_endpoint: `${domainBaseUrl}/oauth2/token`,
          userinfo_endpoint: `${domainBaseUrl}/oauth2/userInfo`,
          end_session_endpoint: `${domainBaseUrl}/logout`,
          jwks_uri: `${config?.auth?.authority}/.well-known/jwks.json`,
        }
      : undefined;

    this.userManager = new UserManager({
      authority: config?.auth?.authority,
      client_id: config?.auth?.clientId,
      redirect_uri: config?.auth?.redirectUri || window.location.origin,
      post_logout_redirect_uri:
        config?.auth?.postLogoutRedirectUri || window.location.origin,
      response_type: "code",
      scope: (config?.auth?.defaultScopes || ["openid", "email"]).join(" "),
      metadata,
      monitorSession: false,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
    debugAuthLog("CognitoAuthClient initialized", {
      authority: config?.auth?.authority,
      cognitoDomain: normalizedDomain,
      clientId: config?.auth?.clientId,
      defaultScopes: config?.auth?.defaultScopes,
    });

    this._accounts = [];
    this._inProgress = this._hasRedirectCallbackParams()
      ? AuthInteractionStatus.Login
      : AuthInteractionStatus.None;
    this._listeners = new Set();
    this._eventCallbacks = new Map();
    this._eventCallbackId = 0;
    this._initialized = false;

    this.userManager.events.addUserLoaded((user) => {
      debugAuthLog("User loaded event", {
        hasUser: !!user,
        expired: !!user?.expired,
        scopes: user?.scopes,
      });
      this._accounts = user ? [toAccount(user)] : [];
      this._emitAuthEvent(AuthEventType.LOGIN_SUCCESS, user);
      this._emitAuthEvent(AuthEventType.ACQUIRE_TOKEN_SUCCESS, user);
      this._notify();
    });
    this.userManager.events.addUserUnloaded(() => {
      this._accounts = [];
      this._notify();
    });
  }

  async initialize() {
    if (this._initialized) {
      return;
    }
    this._initialized = true;
    debugAuthLog("Initialize start", {
      hasRedirectParams: this._hasRedirectCallbackParams(),
    });
    await this._handleRedirectCallbackIfNeeded();
    const user = await this.userManager.getUser();
    debugAuthLog("Initialize user fetched", {
      hasUser: !!user,
      expired: !!user?.expired,
      scopes: user?.scopes,
    });
    this._accounts = user && !user.expired ? [toAccount(user)] : [];
    this._notify();
  }

  addStateListener(listener) {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  addEventCallback(callback) {
    this._eventCallbackId += 1;
    this._eventCallbacks.set(this._eventCallbackId, callback);
    return this._eventCallbackId;
  }

  removeEventCallback(callbackId) {
    this._eventCallbacks.delete(callbackId);
  }

  getAllAccounts() {
    return this._accounts;
  }

  getInProgress() {
    return this._inProgress;
  }

  async loginRedirect(request = {}) {
    debugAuthLog("loginRedirect invoked", {
      scopes: request.scopes || this.config?.auth?.defaultScopes || [],
      extraQueryParams: request.extraQueryParameters || {},
    });
    this._setInProgress(AuthInteractionStatus.Login);
    await this.userManager.signinRedirect({
      extraQueryParams: request.extraQueryParameters || {},
      scope: (request.scopes || this.config?.auth?.defaultScopes || []).join(" "),
    });
  }

  /** Clears the local session and redirects to the Cognito login page. */
  async signInAgain(request = {}) {
    debugAuthLog("signInAgain invoked");
    this._setInProgress(AuthInteractionStatus.Login);
    await this.userManager.removeUser();
    await this.loginRedirect(request);
  }

  async logoutRedirect() {
    this._setInProgress(AuthInteractionStatus.Logout);
    // Cognito managed login /logout requires `client_id` and either `logout_uri` or
    // `redirect_uri` (see AWS "The managed login sign-out endpoint"). OIDC-only params
    // (`post_logout_redirect_uri` + `id_token_hint`) are not enough; oidc-client-ts also
    // omits `client_id` when `id_token_hint` is set, which can yield /login + 400.
    const postLogout =
      this.config?.auth?.postLogoutRedirectUri || `${window.location.origin}/logout`;
    await this.userManager.signoutRedirect({
      client_id: this.config?.auth?.clientId,
      extraQueryParams: {
        logout_uri: postLogout,
      },
    });
  }

  async acquireTokenSilent() {
    this._setInProgress(AuthInteractionStatus.AcquireToken);
    try {
      const user = await this.userManager.getUser();
      debugAuthLog("acquireTokenSilent user lookup", {
        hasUser: !!user,
        expired: !!user?.expired,
        scopes: user?.scopes,
      });
      if (!user || user.expired) {
        throw new InteractionRequiredAuthError(
          "User is not signed in or token is expired",
        );
      }

      this._emitAuthEvent(AuthEventType.ACQUIRE_TOKEN_SUCCESS, user);
      return {
        accessToken: user.access_token,
        idToken: user.id_token,
        account: this._accounts[0],
      };
    } finally {
      this._setInProgress(AuthInteractionStatus.None);
    }
  }

  async acquireTokenRedirect(request = {}) {
    debugAuthLog("acquireTokenRedirect invoked", {
      scopes: request.scopes || this.config?.auth?.defaultScopes || [],
      extraQueryParams: request.extraQueryParameters || {},
    });
    this._setInProgress(AuthInteractionStatus.AcquireToken);
    await this.userManager.signinRedirect({
      extraQueryParams: request.extraQueryParameters || {},
      scope: (request.scopes || this.config?.auth?.defaultScopes || []).join(" "),
    });
  }

  async _handleRedirectCallbackIfNeeded() {
    if (!this._hasRedirectCallbackParams()) {
      return;
    }

    try {
      debugAuthLog("Processing signin redirect callback");
      const user = await this.userManager.signinRedirectCallback();
      debugAuthLog("Signin callback success", {
        hasUser: !!user,
        expired: !!user?.expired,
        scopes: user?.scopes,
      });
      this._accounts = user ? [toAccount(user)] : [];
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      debugAuthLog("Signin callback error", {
        message: error?.message,
        name: error?.name,
      });
      console.error(error);
      // Clean callback params from URL so RequireAuth can initiate a fresh login
      // instead of being stuck detecting stale code=/state= params forever.
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      this._setInProgress(AuthInteractionStatus.None);
    }
  }

  _hasRedirectCallbackParams() {
    const search = window.location.search || "";
    return search.includes("code=") && search.includes("state=");
  }

  _emitAuthEvent(eventType, user) {
    const payload = {
      eventType,
      payload: {
        account: toAccount(user),
      },
    };
    this._eventCallbacks.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error(error);
      }
    });
  }

  _setInProgress(status) {
    this._inProgress = status;
    this._notify();
  }

  _notify() {
    this._listeners.forEach((listener) => {
      listener({
        accounts: this._accounts,
        inProgress: this._inProgress,
      });
    });
  }
}
